import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { EmptyConversationView } from "~/pages/manifold-nextgen/components/conversations/EmptyConversationView";
import { Composer } from "~/pages/manifold-nextgen/components/conversations/composer/Composer";
import { useConnectors } from "~/pages/manifold-nextgen/components/conversations/connectors/useConnectors";
import { stashPendingFirstMessage } from "~/pages/manifold-nextgen/components/conversations/pendingFirstMessage";
import { useSkills } from "~/pages/manifold-nextgen/components/conversations/skills/useSkills";
import { nextgenConversationPath } from "~/pages/manifold-nextgen/paths";
import { Agent, AgentStatus } from "~/services/agents";
import { uploadFilesToConversation } from "~/services/artifacts";
import { deleteConversation } from "~/services/conversations";
import { createProjectConversation, projectConversationsPath } from "~/services/projects";
import { useProjectContext } from "./useProjectContext";

interface AgentListProps {
  agents: Agent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const AgentList = ({ agents, selectedId, onSelect }: AgentListProps) => (
  <RadioGroup value={selectedId ?? ""} onValueChange={onSelect} className="block">
    <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg">
      {agents.map((agent) => (
        <li key={agent.id}>
          <label className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50">
            <RadioGroupIndicator value={agent.id} />
            <AgentAvatar agent={agent} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{agent.display_name}</p>
              {agent.description && (
                <p className="truncate text-xs text-muted-foreground">{agent.description}</p>
              )}
            </div>
          </label>
        </li>
      ))}
    </ul>
  </RadioGroup>
);

// "New Session" splash for a configured project. Mirrors the empty-conversation
// layout — large agent avatar, starter prompts, centered composer — so starting
// a session is just: pick an agent, type, and send. The conversation is created
// lazily on that first submit; the typed text rides in as its first message.
export const NewSessionPicker = () => {
  const { projectId, attachedAgents, attachedAgentsWithDetails, sessions } = useProjectContext();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  const [starterPromptValue, setStarterPromptValue] = useState<string | undefined>(undefined);

  // Only ENABLED agents can start a new session. DISABLED and soft-DELETED
  // agents still surface their historic conversations in the sidebar, but
  // neither may be picked here to begin a fresh one.
  const enabledAgents = useMemo(
    () => (attachedAgents ?? []).filter((a) => a.status === AgentStatus.ENABLED),
    [attachedAgents]
  );

  // The agent from the most recent session, if it's still enabled — used to
  // pre-select the agent the user last worked with when returning to a project.
  const lastUsedAgentId = useMemo(() => {
    const lastSession = sessions?.find((s) => s.agent?.id);
    return lastSession?.agent?.id ?? null;
  }, [sessions]);

  // Effective selection: an explicit pick when valid, then the last-used agent
  // if still enabled, then the first available. Deriving the default (rather
  // than syncing it through an effect) means the right agent is chosen on the
  // first paint — no flash of the "no agents" fallback — and a detached
  // selection falls back automatically.
  const effectiveAgentId =
    enabledAgents.find((a) => a.id === selectedAgentId)?.id ??
    enabledAgents.find((a) => a.id === lastUsedAgentId)?.id ??
    enabledAgents[0]?.id ??
    null;

  const selectedAgent = enabledAgents.find((a) => a.id === effectiveAgentId) ?? null;

  // Same hook the live conversation uses: skills resolve from the project and
  // selected agent, so the splash picker matches the in-conversation one.
  const { skills } = useSkills(projectId, effectiveAgentId);
  const starterPrompts =
    attachedAgentsWithDetails?.find((a) => a.id === effectiveAgentId)?.starter_prompts ?? [];

  // Connector selection is keyed by `projectId:agentId` and persisted (the
  // toggles in localStorage, the guidelines acceptance on the backend), so a
  // choice made here on the splash carries into the conversation we create and
  // is forwarded on its first turn — no need to thread it through navigation.
  const { connectors, selectedConnectorNames, toggleConnector, mcpGuidelines } = useConnectors(
    effectiveAgentId,
    projectId
  );

  // A ref, not `isPending`, gates re-entry: two Enter presses in the same tick
  // both read the stale pre-render `isPending`, so a state flag alone can't stop
  // a double create. `isPending` still drives the composer's disabled state.
  const startingRef = useRef(false);

  const handleStart = async (text: string, files: File[]) => {
    if (startingRef.current) {
      return;
    }
    if (!effectiveAgentId) {
      showError("This project needs an agent before you can start a session.");
      return;
    }
    startingRef.current = true;
    setIsPending(true);
    try {
      const conversation = await createProjectConversation(projectId, {
        agent_id: effectiveAgentId,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      // No conversation existed while composing, so the files were held raw.
      // Upload them now; if any fails, delete the just-created conversation so a
      // failed attachment doesn't strand an empty session in the project (the
      // delete cascades to any artifacts that did land).
      let initialAttachments: string[];
      try {
        initialAttachments = await uploadFilesToConversation(conversation.id, files);
      } catch (uploadErr) {
        await deleteConversation(conversation.id);
        throw uploadErr;
      }
      await mutate(projectConversationsPath(projectId));
      // Queue the first turn for the new conversation to consume on mount; its
      // presence also marks the conversation "new" for the empty-state skeleton.
      stashPendingFirstMessage(conversation.id, {
        message: text,
        attachmentIds: initialAttachments,
      });
      navigate(nextgenConversationPath(projectId, conversation.id));
    } catch (err) {
      startingRef.current = false;
      setIsPending(false);
      // Re-raise so the composer keeps the held files and reports the failure,
      // letting the user retry without re-selecting them.
      throw err;
    }
  };

  // No ENABLED agent to start a session with (all disabled/deleted). This is a
  // real empty state, distinct from "an agent exists but isn't selected yet" —
  // gating only on the count avoids flashing this while a default resolves.
  if (enabledAgents.length === 0 || !selectedAgent) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold">No agents available</h1>
          <p className="text-sm text-muted-foreground">
            Every agent on this project is disabled. Ask a project owner to enable one in Settings.
          </p>
        </div>
      </div>
    );
  }

  const agentSwitcher = enabledAgents.length > 1 && (
    <Popover open={agentPopoverOpen} onOpenChange={setAgentPopoverOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Switch agent"
              className="size-8 shrink-0"
            >
              <ChevronDown className="size-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Switch agent</TooltipContent>
      </Tooltip>
      <PopoverContent align="center" className="w-80 p-0">
        <AgentList
          agents={enabledAgents}
          selectedId={effectiveAgentId}
          onSelect={(id) => {
            setSelectedAgentId(id);
            setAgentPopoverOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );

  return (
    <EmptyConversationView
      agent={selectedAgent}
      nameAction={agentSwitcher || undefined}
      starterPrompts={starterPrompts}
      onPromptClick={setStarterPromptValue}
      anchorTop
    >
      <Composer
        onSubmit={handleStart}
        disabled={isPending}
        starterPromptValue={starterPromptValue}
        skills={skills}
        connectors={connectors}
        enabledConnectorNames={selectedConnectorNames}
        onToggleConnector={toggleConnector}
        mcpGuidelines={mcpGuidelines}
      />
    </EmptyConversationView>
  );
};
